import { BookmarkPlus, ChevronLeft, ChevronRight, ChevronUp, FileSpreadsheet, MoreVertical, Plus, Redo2, Undo2, Zap } from "lucide-react";
import { ChangeEvent, Dispatch, ReactNode, Ref, useCallback, useEffect, useMemo, useRef, useState } from "react";

import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@buildpy/ui/dropdown-menu";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { cn } from "@/lib/utils";
import { QuoteAmounts } from "@buildpy/core/modules/quote/action-types";
import { MissingSuggestion } from "@buildpy/core/modules/quote/missing-rules";
import { formatQty } from "@/modules/quote/ui/format";
import Money from "@/components/money";
import AreaInput from "@/modules/quote/ui/editor/area-input";
import CoverEditor from "@/modules/quote/ui/editor/cover-editor";
import { HistoryAction } from "@buildpy/core/modules/quote/editor/history";
import MobileItemSheet from "@/modules/quote/ui/editor/mobile/mobile-item-sheet";
import MobileTrade from "@/modules/quote/ui/editor/mobile/mobile-trade";
import PhoneSheet from "@/modules/quote/ui/editor/mobile/phone-sheet";
import { lockedNow, openUnlessLocked, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";
import SchedulePanel, { PhaseOption, ScheduleStandard, TradePhaseRef } from "@/modules/quote/ui/editor/schedule-panel";
import type { ProcessCalibration } from "@buildpy/core/modules/process-standard/calibration";
import TotalsPanel, { CostSummary } from "@/modules/quote/ui/editor/totals-panel";
import VatModeField from "@/modules/quote/ui/editor/vat-mode-field";
import { stateAreaM2, vatModeOf, vatSwitchPriceCount } from "@buildpy/core/modules/quote/editor/reducer";
import { QuoteStatus } from "@buildpy/core/modules/quote/types";
import { EditorState, EditorTrade, ItemRef } from "@buildpy/core/modules/quote/editor/types";
import { TRADE_GROUP_LABEL, TRADE_GROUPS, TradeGroup } from "@buildpy/core/modules/template/trades";
import { TemplateItem } from "@buildpy/core/modules/template/types";
import { lockedDescribedBy } from "@/modules/quote/ui/editor/locked-banner";

interface HeadProps {
  quoteNo: string;
  version: number;
  // phoneAutosaveLabelOf 결과(「 · 임시 저장됨」) — 데스크톱보다 짧은 폰 문구.
  status: string;
  retry: (() => void) | null;
  canUndo: boolean;
  canRedo: boolean;
  simple: boolean;
  setSimple: (next: boolean) => void;
  showCost: boolean;
  setShowCost: (next: boolean) => void;
  dispatch: Dispatch<HistoryAction>;
  onBack: () => void;
  // 데스크톱 「빠른 추가」 바를 시트로 연다 — 공종이 없으면 null(데스크톱도 바를 숨긴다).
  onQuickAdd: (() => void) | null;
  // 데스크톱 「엑셀 가져오기」 와 같은 대화상자를 연다.
  onImport: () => void;
  // 대표만 — 아니면 null 이라 메뉴에서 뺀다.
  onSaveTemplate: (() => void) | null;
  // 「더보기」 단추 — 메뉴에서 연 창·시트가 닫히면 편집기가 여기로 포커스를 돌린다.
  moreRef: Ref<HTMLButtonElement>;
  // 잠긴 견적 — 되돌리기·빠른 추가·엑셀 가져오기를 끈다(보기 설정·템플릿 저장은 데스크톱처럼 그대로).
  locked?: boolean;
}

// 폰 머리줄 — 뒤로(상세로) · 「견적 편집」 · 번호와 자동저장 상태 · 더보기(되돌리기·빠른 추가·엑셀·템플릿·단가 입력 방식).
export const MobileEditorHead = ({
  quoteNo,
  version,
  status,
  retry,
  canUndo,
  canRedo,
  simple,
  setSimple,
  showCost,
  setShowCost,
  dispatch,
  onBack,
  onQuickAdd,
  onImport,
  onSaveTemplate,
  moreRef,
  locked = false,
}: HeadProps) => {
  // dispatch 는 편집기가 lockedRef 를 그때 읽는 dispatchUnlessLocked 로 넘긴다 — 여기서 다시 감싸지 않는다.
  return (
    <div className="flex items-center gap-x-1 md:hidden" data-testid="phone-editor-head">
      <Button aria-label="상세로" className="-ml-2 shrink-0" size="icon" variant="ghost" onClick={onBack}>
        <ChevronLeft />
      </Button>
      <div className="flex min-w-0 flex-1 flex-col">
        <h1 className="text-lg font-semibold tracking-tight">견적 편집</h1>
        <span className="truncate text-xs text-muted-foreground">
          {quoteNo} v{version}
          {status}
        </span>
        {retry && (
          <Button className="self-start" size="xs" variant="link-destructive" onClick={retry}>
            저장 실패 — 다시 시도
          </Button>
        )}
      </div>
      {/* 메뉴에서 대화상자·시트를 여므로 비모달 — 모달이면 닫히며 body 의 pointer-events 가 남는다. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button ref={moreRef} aria-label="더보기" className="shrink-0" size="icon" variant="ghost">
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={!canUndo || locked} onSelect={() => dispatch({ type: "UNDO" })}>
            <Undo2 className="size-4" /> 되돌리기
          </DropdownMenuItem>
          <DropdownMenuItem disabled={!canRedo || locked} onSelect={() => dispatch({ type: "REDO" })}>
            <Redo2 className="size-4" /> 다시 실행
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={onQuickAdd === null || locked} onSelect={() => onQuickAdd?.()}>
            <Zap className="size-4" /> 빠른 추가
          </DropdownMenuItem>
          <DropdownMenuItem disabled={locked} onSelect={onImport}>
            <FileSpreadsheet className="size-4" /> 엑셀 가져오기
          </DropdownMenuItem>
          {onSaveTemplate && (
            <DropdownMenuItem onSelect={onSaveTemplate}>
              <BookmarkPlus className="size-4" /> 템플릿으로 저장
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuCheckboxItem checked={!simple} onCheckedChange={(checked) => setSimple(!checked)}>
            단가를 자재·노무로 나눠 입력
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem checked={showCost} onCheckedChange={(checked) => setShowCost(checked === true)}>
            원가·마진 보기
          </DropdownMenuCheckboxItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

interface TradeOption {
  code: string;
  name: string;
  group: TradeGroup;
  sort_order: number;
}

interface Props {
  state: EditorState;
  dispatch: Dispatch<HistoryAction>;
  simple: boolean;
  setSimple: (next: boolean) => void;
  spaces: { code: string; label: string }[];
  // 원가·마진 칸과 합계의 원가 묶음 · 계산 결과(원가 합계·이익) · 견적 상태(부가세 전환 잠금).
  showCost: boolean;
  cost: CostSummary | null;
  status: QuoteStatus;
  customerName: string;
  customerSite: string;
  validDays: number;
  // 저장 전이면 화면 계산값, 아니면 저장값 — 데스크톱 합계 칸과 같은 값.
  shown: QuoteAmounts;
  autoRounding: number;
  live: boolean;
  needsArea: boolean;
  pctError: string | null;
  templateByTrade: ReadonlyMap<string, TemplateItem[]>;
  // 공종 뼈대뿐일 때의 「기본 템플릿 항목 한 번에 넣기」 안내 — 넣을 수 없으면 null.
  fillNote: string | null;
  missing: MissingSuggestion[];
  ignoredCount: number;
  canAddMissing: (s: MissingSuggestion) => boolean;
  onAddMissing: (s: MissingSuggestion) => void;
  onIgnoreMissing: (id: string) => void;
  onResetIgnored: () => void;
  missingTrades: TradeOption[];
  phases: PhaseOption[];
  tradePhases: TradePhaseRef[];
  // 시공일정 면적 초안(#170) — 공정 표준 판·회사 보정.
  standard?: ScheduleStandard | null;
  calibration?: ProcessCalibration;
  onSuggest: (ref: ItemRef) => void;
  saveDisabled: boolean;
  lockNote?: string;
  // 편집기 lockedRef 를 그때 읽는 잠김 — 늦게 오는 일정 응답 판정용.
  isLocked: () => boolean;
  onSave: () => void;
  // 데스크톱과 같은 QuickAdd 요소 — 머리 더보기의 「빠른 추가」 가 이 시트를 연다.
  quickAdd: ReactNode;
  quickOpen: boolean;
  onQuickOpenChange: (open: boolean) => void;
  // 빠른 추가 시트가 닫히면 연 단추(머리 더보기)로 — 시트는 열릴 때 자기 안으로 포커스를 넣는다.
  quickReturnFocus: () => void;
}

// 펼친 공종은 카드 하나씩, 이어지는 접힌 공종은 한 카드에 줄로 쌓는다.
const chunksOf = (trades: EditorTrade[]): EditorTrade[][] =>
  trades.reduce<EditorTrade[][]>((out, trade) => {
    const last = out.at(-1);
    if (trade.collapsed && last && last[0].collapsed) last.push(trade);
    else out.push([trade]);
    return out;
  }, []);

// 폰(md 미만) 견적 편집 본문 — 상태·동작은 데스크톱과 같은 reducer·dispatch 를 그대로 쓴다.
const MobileEditor = ({
  state,
  dispatch,
  simple,
  setSimple,
  spaces,
  showCost,
  cost,
  status,
  customerName,
  customerSite,
  validDays,
  shown,
  autoRounding,
  live,
  needsArea,
  pctError,
  templateByTrade,
  fillNote,
  missing,
  ignoredCount,
  canAddMissing,
  onAddMissing,
  onIgnoreMissing,
  onResetIgnored,
  missingTrades,
  phases,
  tradePhases,
  standard,
  calibration,
  onSuggest,
  saveDisabled,
  lockNote,
  isLocked,
  onSave,
  quickAdd,
  quickOpen,
  onQuickOpenChange,
  quickReturnFocus,
}: Props) => {
  const [selected, setSelected] = useState<ItemRef | null>(null);
  const [coverOpen, setCoverOpen] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const [totalsOpen, setTotalsOpen] = useState(false);
  const [editTradeKey, setEditTradeKey] = useState<string | null>(null);
  // 「+ 공종」 메뉴 — 잠기면 열려 있어도 닫는다(포털이라 fieldset 이 못 끈다).
  const [tradeMenuOpen, setTradeMenuOpen] = useState(false);
  // 잠긴 견적 — 데스크톱 본문 fieldset 과 같이 읽기 전용(시트는 포털이라 각자 disabled 를 받는다).
  const locked = lockNote !== undefined;
  // 부르는 순간의 편집기 잠김(lockedRef)도 본다 — 재렌더 전 클릭·늦은 onChange 막이.
  const dispatchUnlessLocked = useMemo(() => unlessLocked(lockedNow(locked, isLocked), dispatch), [locked, isLocked, dispatch]);

  // 「+ 항목」·「세트 하위 추가」 뒤 새 줄을 시트로 연다 — 키는 리듀서가 만들므로 상태가 바뀐 뒤 마지막 줄을 찾는다.
  const pendingRef = useRef<{ tradeKey: string; parentKey: string | null } | null>(null);
  useEffect(() => {
    const pending = pendingRef.current;
    if (pending === null) return;
    pendingRef.current = null;
    const trade = state.trades.find((t) => t.key === pending.tradeKey);
    const list = pending.parentKey ? trade?.items.find((i) => i.key === pending.parentKey)?.children : trade?.items;
    const last = list?.at(-1);
    if (last) setSelected({ tradeKey: pending.tradeKey, itemKey: last.key, parentKey: pending.parentKey });
  }, [state.trades]);

  const addItem = useCallback(
    (tradeKey: string) => {
      if (locked || isLocked()) return;
      pendingRef.current = { tradeKey, parentKey: null };
      dispatchUnlessLocked({ type: "ADD_ITEM", tradeKey });
    },
    [locked, isLocked, dispatchUnlessLocked]
  );
  const addChild = useCallback(
    (tradeKey: string, parentKey: string) => {
      if (locked || isLocked()) return;
      pendingRef.current = { tradeKey, parentKey };
      dispatchUnlessLocked({ type: "ADD_CHILD", tradeKey, parentKey });
    },
    [locked, isLocked, dispatchUnlessLocked]
  );

  const selectedTrade = selected ? state.trades.find((t) => t.key === selected.tradeKey) : undefined;
  const editTrade = editTradeKey ? state.trades.find((t) => t.key === editTradeKey) : undefined;
  const missingNames = useMemo(() => missing.map((s) => s.suggestion.name).join(" · "), [missing]);
  const coverCaption = [
    customerName,
    state.area_pyeong === null ? "면적 없음" : `${formatQty(stateAreaM2(state) ?? 0)}m² (${formatQty(state.area_pyeong)}평)`,
    `관리비 ${state.mgmt_fee_pct === "" ? "—" : state.mgmt_fee_pct}%`,
    simple ? "단가 간단" : "단가 상세",
  ].join(" · ");

  return (
    // 칸을 떠나면 입력 묶음을 끊는다 — 시트는 포털이지만 React 트리로 이 자리까지 올라온다.
    <div className="flex flex-col gap-y-2.5 md:hidden" data-testid="phone-editor" onBlurCapture={() => dispatch({ type: "SEAL" })}>
      <Button
        className="flex w-full items-center justify-start text-left"
        data-testid="phone-cover-card"
        size="row"
        variant="card"
        onClick={() => setCoverOpen(true)}>
        <span className="flex min-w-0 flex-1 flex-col gap-y-0.5">
          <span className={cn("truncate font-semibold", state.title.trim() === "" && "text-muted-foreground")}>
            {state.title.trim() === "" ? "공사명 없음" : state.title}
          </span>
          <span className={cn("truncate text-xs", needsArea || pctError ? "text-destructive" : "text-muted-foreground")}>{coverCaption}</span>
        </span>
        <ChevronRight className="text-muted-foreground" />
      </Button>

      {missing.length > 0 && (
        <div className="flex items-center gap-x-2 rounded-md bg-accent px-3 py-2 text-sm text-accent-foreground" data-testid="phone-missing-hint">
          <span className="min-w-0 flex-1 truncate">
            빠진 항목 {missing.length} — {missingNames}
          </span>
          <Button className="shrink-0" size="inline" variant="link" onClick={() => setMissingOpen(true)}>
            보기
          </Button>
        </div>
      )}
      {missing.length === 0 && ignoredCount > 0 && (
        <Button className="self-start" size="xs" variant="link-muted" onClick={onResetIgnored}>
          무시한 {ignoredCount}개 다시 보기
        </Button>
      )}

      {fillNote !== null && (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2">
          <span className="text-sm text-muted-foreground">{fillNote}</span>
          <Button disabled={locked} size="sm" variant="outline" onClick={() => dispatchUnlessLocked({ type: "FILL_TEMPLATE", byTrade: templateByTrade })}>
            <Plus /> 기본 템플릿 항목 한 번에 넣기
          </Button>
        </div>
      )}

      {TRADE_GROUPS.map((group) => {
        const trades = state.trades.filter((t) => t.group === group);
        const subtotal = group === "BASIC" ? shown.subtotal_basic : shown.subtotal_style;
        return (
          <section key={group} className="flex flex-col gap-y-2" data-testid="phone-trade-group">
            <div className="flex items-baseline justify-between px-1 pt-1">
              <h2 className="text-base font-semibold">{TRADE_GROUP_LABEL[group]}</h2>
              <Money className="text-sm text-muted-foreground" value={subtotal} />
            </div>
            {trades.length === 0 && <p className="px-1 text-sm text-muted-foreground">공종이 없습니다.</p>}
            {chunksOf(trades).map((chunk) => (
              <SectionCard key={chunk[0].key} as="div" className="divide-y divide-border overflow-hidden" padding="none">
                {chunk.map((trade) => (
                  <MobileTrade
                    key={trade.key}
                    disabled={locked}
                    dispatch={dispatch}
                    isLocked={isLocked}
                    selectedKey={selected?.tradeKey === trade.key ? selected.itemKey : null}
                    simple={simple}
                    templateItems={templateByTrade.get(trade.trade) ?? []}
                    trade={trade}
                    onAddItem={addItem}
                    onEditTrade={setEditTradeKey}
                    onOpenItem={setSelected}
                  />
                ))}
              </SectionCard>
            ))}
          </section>
        );
      })}

      <SchedulePanel
        areaM2={stateAreaM2(state)}
        calibration={calibration}
        disabled={locked}
        dispatch={dispatch}
        isLocked={isLocked}
        phases={phases}
        schedule={state.schedule}
        standard={standard}
        tradePhases={tradePhases}
        trades={state.trades}
      />

      <fieldset aria-describedby={lockedDescribedBy(locked)} className="min-w-0" disabled={locked}>
        <CoverEditor
          conditionKeys={state.condition_keys}
          conditions={state.conditions}
          disabled={locked}
          dispatch={dispatch}
          extraNotice={state.extra_notice}
          isLocked={isLocked}
        />
      </fieldset>

      <div
        className="sticky bottom-0 z-10 -mx-4 flex items-center gap-x-2 border-t border-border bg-card px-4 pb-safe-2 pt-2"
        data-testid="phone-editor-bar">
        <Button
          aria-haspopup="dialog"
          className="flex min-w-0 shrink flex-col items-start text-left"
          size="none-style"
          variant="none-style"
          onClick={() => setTotalsOpen(true)}>
          <span className="flex items-center gap-x-0.5 text-xs text-muted-foreground">
            최종 견적금액 <ChevronUp />
          </span>
          <Money className="truncate text-lg font-bold" value={shown.total} />
        </Button>
        <DropdownMenu open={openUnlessLocked(tradeMenuOpen, locked)} onOpenChange={setTradeMenuOpen}>
          {/* 비활성은 Trigger 에 단다 — 자식 Button 에만 달면 Radix 가 포인터다운으로 빈 메뉴를 연다. */}
          <DropdownMenuTrigger asChild disabled={missingTrades.length === 0 || locked}>
            <Button className="ml-auto shrink-0" variant="outline">
              <Plus /> 공종
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-80 overflow-auto">
            {missingTrades.map((def) => (
              <DropdownMenuItem
                key={def.code}
                onSelect={() =>
                  dispatchUnlessLocked({ type: "ADD_TRADE", trade: def.code, name: def.name, group: def.group, sort_order: def.sort_order })
                }>
                {def.name}
                <span className="text-xs text-muted-foreground">{TRADE_GROUP_LABEL[def.group]}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* 비활성 단추는 포인터를 안 받아 title 이 안 뜨므로 감싼 span 에 단다. */}
        <span className="flex w-28 shrink-0" title={lockNote}>
          <Button className="w-full" disabled={saveDisabled} variant="primary" onClick={onSave}>
            저장
          </Button>
        </span>
      </div>

      <MobileItemSheet
        area={state.area_pyeong}
        areaM2={stateAreaM2(state)}
        disabled={locked}
        dispatch={dispatch}
        isLocked={isLocked}
        setSimple={setSimple}
        simple={simple}
        spaces={spaces}
        target={selected}
        trade={selectedTrade}
        onAddChild={addChild}
        onSelect={setSelected}
        onSuggest={onSuggest}
        showCost={showCost}
        vatMode={vatModeOf(state)}
      />

      <PhoneSheet
        description="공사명·면적·관리비율을 고칩니다"
        disabled={locked}
        footer={
          <Button className="flex-1" variant="primary" onClick={() => setCoverOpen(false)}>
            완료
          </Button>
        }
        open={coverOpen}
        title="견적 기본 정보"
        onOpenChange={setCoverOpen}>
        <Field label="공사명">
          <Input disabled={locked} inputSize="lg" name="title" value={state.title} onChange={(e) => dispatchUnlessLocked({ type: "SET_TITLE", value: e.target.value })} />
        </Field>
        <div className="flex flex-col gap-y-1">
          <span className="text-xs text-muted-foreground">고객</span>
          <p className="text-sm">
            {customerName}
            {customerSite && <span className="text-muted-foreground"> · {customerSite}</span>}
          </p>
        </div>
        <div className="flex flex-col gap-y-1">
          <span className="text-xs text-muted-foreground">유효기간</span>
          <p className="text-sm">발송일로부터 {validDays}일</p>
        </div>
        <div className="[&_input]:min-h-11 [&_input]:text-lg">
          <AreaInput disabled={locked} invalid={needsArea} value={stateAreaM2(state)} onChange={(value) => dispatchUnlessLocked({ type: "SET_AREA_M2", value })} />
        </div>
        <Field label="관리비율 (%)">
          <div className="w-28">
            <Input
              aria-invalid={pctError !== null}
              className="text-right tabular-nums"
              disabled={locked}
              inputMode="decimal"
              inputSize="lg"
              name="mgmt_fee_pct"
              value={state.mgmt_fee_pct}
              onChange={(e) => dispatchUnlessLocked({ type: "SET_MGMT_PCT", value: e.target.value })}
            />
          </div>
          {pctError && <span className="text-xs text-destructive">{pctError}</span>}
        </Field>
        <VatModeField
          disabled={locked}
          isLocked={isLocked}
          priceCount={vatSwitchPriceCount(state)}
          size="lg"
          status={status}
          value={vatModeOf(state)}
          onChange={(value) => dispatchUnlessLocked({ type: "SET_VAT_MODE", value, status })}
        />
      </PhoneSheet>

      <PhoneSheet
        description="공종 이름과 견적서에 붙는 조건 문구를 고칩니다"
        disabled={locked}
        footer={
          <Button className="flex-1" variant="primary" onClick={() => setEditTradeKey(null)}>
            완료
          </Button>
        }
        open={editTrade !== undefined}
        title="공종 편집"
        onOpenChange={(open) => {
          if (!open) setEditTradeKey(null);
        }}>
        {editTrade && (
          <>
            <Field label="공종명">
              <Input
                disabled={locked}
                inputSize="lg"
                name="trade_name"
                value={editTrade.name}
                onChange={(e: ChangeEvent<HTMLInputElement>) => dispatchUnlessLocked({ type: "SET_TRADE_NAME", tradeKey: editTrade.key, value: e.target.value })}
              />
            </Field>
            <Field label="조건 문구">
              <Textarea
                className="min-h-24 text-lg"
                disabled={locked}
                value={editTrade.condition_text}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => dispatchUnlessLocked({ type: "SET_TRADE_CONDITION", tradeKey: editTrade.key, value: e.target.value })}
              />
            </Field>
          </>
        )}
      </PhoneSheet>

      {/* 잠겨도 시트 전체를 끄지 않는다 — 무시·무시 해제는 보기 설정, 「추가」 만 끈다. */}
      <PhoneSheet description="규칙으로 찾은 빠진 항목을 추가하거나 무시합니다" open={missingOpen} title={`빠진 항목 ${missing.length}`} onOpenChange={setMissingOpen}>
        {missing.length === 0 && <p className="text-sm text-muted-foreground">빠진 항목이 없습니다.</p>}
        <ul className="flex flex-col divide-y divide-border">
          {missing.map((s) => {
            const addable = canAddMissing(s);
            return (
              <li key={s.id} className="flex flex-col gap-y-2 py-2.5 text-sm">
                <span className="flex flex-col">
                  <span>{s.message}</span>
                  <span className="text-xs text-muted-foreground">
                    제안: {s.suggestion.name} · {s.suggestion.unit}
                    {s.suggestion.qty !== undefined ? ` · ${s.suggestion.qty}` : ""}
                  </span>
                </span>
                <span className="flex gap-2">
                  <Button className="flex-1" disabled={!addable || locked} size="sm" title={lockNote} variant="outline" onClick={unlessLocked(lockedNow(locked, isLocked), () => onAddMissing(s))}>
                    추가
                  </Button>
                  <Button className="flex-1" size="sm" variant="ghost" onClick={() => onIgnoreMissing(s.id)}>
                    무시
                  </Button>
                </span>
              </li>
            );
          })}
        </ul>
        {ignoredCount > 0 && (
          <Button className="self-start" size="xs" variant="link-muted" onClick={onResetIgnored}>
            무시한 {ignoredCount}개 다시 보기
          </Button>
        )}
      </PhoneSheet>

      <PhoneSheet
        description="공종을 골라 작업내역·수량·단가를 한 줄씩 담습니다. 지난 단가 제안과 엑셀 붙여넣기도 됩니다"
        disabled={locked}
        footer={
          <Button className="flex-1" variant="primary" onClick={() => onQuickOpenChange(false)}>
            완료
          </Button>
        }
        open={quickOpen}
        returnFocus={quickReturnFocus}
        title="빠른 추가"
        onOpenChange={onQuickOpenChange}>
        {/* 데스크톱 바를 그대로 쓰되 입력칸만 44px·16px 로 키운다(iOS 확대 방지). */}
        <div className="[&_input]:min-h-11 [&_input]:text-lg" data-testid="phone-quick-add">
          {quickAdd}
        </div>
      </PhoneSheet>

      <PhoneSheet description="소계·관리비·절사·최종 견적금액" disabled={locked} open={totalsOpen} title="금액 상세" onOpenChange={setTotalsOpen}>
        <TotalsPanel
          amounts={shown}
          autoRounding={autoRounding}
          cost={cost}
          disabled={locked}
          dispatch={dispatch}
          isLocked={isLocked}
          live={live}
          rounding={state.rounding}
          showCost={showCost}
          vatMode={vatModeOf(state)}
        />
      </PhoneSheet>
    </div>
  );
};

export default MobileEditor;
